import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Table } from 'antd';
import { FilePdfOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../../../utils/helpers/bootstrapApi.jsx';
import {
  useGetMyApplicationsQuery,
  useDeleteApplicationMutation,
} from '../../../../../redux/services/ing_projects.jsx';
import { routes, urls } from '../../../../../utils/helpers/ApiClient.jsx';
import { CollapseBlock, Spinner, QuestionModal } from '../../../../common/index.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import IconButton from '../../../../common/controls/IconButton/IconButton.jsx';
import ApplyToProjectModal from '../IngProjectsPage/ApplyToProjectModal.jsx';

const IngProjectsMyApplicationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { errorOccurred, successMessage, apiErrorMessage } = useToastMessages();
  const [isDownloading, setIsDownloading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editApplication, setEditApplication] = useState(null);

  useEffect(() => {
    document.title = t('Vybrané projekty') + ' | FRI UNIZA';
  }, [t]);

  const { data: applications, isLoading } = useGetMyApplicationsQuery();
  const [deleteApplication] = useDeleteApplicationMutation();

  // Group applications by study program
  const groupedApplications = useMemo(() => {
    if (!applications || applications.length === 0) return [];

    const groups = {};
    applications.forEach((app) => {
      const programId = app.study_program_id;
      const programName = app.study_program?.name || t('Neznámy program');

      if (!groups[programId]) {
        groups[programId] = {
          programId,
          programName,
          applications: [],
        };
      }
      groups[programId].applications.push(app);
    });

    // Sort applications within each group by priority
    Object.values(groups).forEach((group) => {
      group.applications.sort((a, b) => a.priority - b.priority);
    });

    // Return sorted by program name
    return Object.values(groups).sort((a, b) =>
      a.programName.localeCompare(b.programName),
    );
  }, [applications, t]);

  const handleDeleteClick = (applicationId) => {
    setDeleteId(applicationId);
  };

  const handleEditClick = (application) => {
    setEditApplication(application);
  };

  const handleEditModalClose = () => {
    setEditApplication(null);
  };

  const onConfirmDelete = async () => {
    try {
      await deleteApplication(deleteId).unwrap();
      successMessage({ message: t('Prihláška bola úspešne zmazaná.') });
    } catch (error) {
      apiErrorMessage(error);
    } finally {
      setDeleteId(null);
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get(urls.ingProjectsMyApplicationsPdf, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `IngProjekty${new Date().getFullYear()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      errorOccurred();
    } finally {
      setIsDownloading(false);
    }
  };

  // Columns without study program (since it's in the section title)
  const columns = useMemo(
    () => [
      {
        title: t('Priorita'),
        dataIndex: 'priority',
        key: 'priority',
        width: 100,
      },
      {
        title: t('Názov projektu'),
        dataIndex: ['project', 'name'],
        key: 'project_name',
        render: (text, record) => (
          <a
            onClick={() =>
              navigate(routes.cmsIngProjectsDetail(record.project?.id))
            }
          >
            {text}
          </a>
        ),
      },
      {
        title: t('Priorita učiteľa'),
        dataIndex: 'teacher_priority',
        key: 'teacher_priority',
        width: 130,
      },
      {
        title: t('Akcie'),
        key: 'actions',
        width: 100,
        render: (_, record) => (
          <div style={{ display: 'flex' }}>
            <IconButton
              icon={<EditOutlined />}
              tooltip={t('Upraviť prihlášku')}
              onClick={() => handleEditClick(record)}
            />
            <IconButton
              icon={<DeleteOutlined />}
              tooltip={t('Zmazať prihlášku')}
              onClick={() => handleDeleteClick(record.id)}
            />
          </div>
        ),
      },
    ],
    [t, navigate],
  );

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={t('Vybrané projekty')}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
      />

      <div style={{ margin: '10px' }}>
        {applications && applications.length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleDownloadPdf}
              loading={isDownloading}
            >
              {t('Vytlačiť tlačivo')}
            </Button>
          </div>
        )}

        {groupedApplications.length > 0 ? (
          groupedApplications.map((group) => (
            <CollapseBlock
              key={group.programId}
              title={`${group.programName} (${group.applications.length})`}
            >
              <Table
                rowKey={(record) => record.id}
                columns={columns}
                dataSource={group.applications}
                pagination={false}
                size="small"
              />
            </CollapseBlock>
          ))
        ) : (
          <CollapseBlock title={t('Moje prihlášky na projekty')}>
            <p>{t('Nemáte žiadne prihlášky na projekty')}</p>
          </CollapseBlock>
        )}
      </div>

      <QuestionModal
        visible={deleteId !== null}
        toggle={() => setDeleteId(null)}
        chosenPositive={onConfirmDelete}
        headerTitle={t('Zmazať prihlášku')}
        question={t('Naozaj chcete zmazať túto prihlášku?')}
        yes={t('Áno')}
      />

      <ApplyToProjectModal
        visible={editApplication !== null}
        onClose={handleEditModalClose}
        application={editApplication}
      />
    </>
  );
};

export default IngProjectsMyApplicationsPage;
